<!-- EnhancedSelect.vue -->
<template>
  <el-select 
    v-bind="$attrs"
    v-model="selectedValue"
    :class="className"
    :placeholder="placeholder"
    :filterable="filterable"
    :filter-method="customFilterMethod"
    @visible-change="handleVisibleChange"
    @change="handleChange"
    v-on="$listeners"
    clearable
    :disabled="disabled"
  >
    <el-option 
      v-for="(item, index) in displayOptions" 
      :key="`${item[valueKey]}_${index}`" 
      :label="item[labelKey]"
      :value="item[valueKey]"
    >
    </el-option>
  </el-select>
</template>

<script>
export default {
  name: 'EnhancedSelect',
  
  props: {
    // v-model 绑定的值
    value: {
      type: [String, Number],
      default: ''
    },
    // 选项数据源
    options: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // 占位符
    placeholder: {
      type: String,
      default: '请选择'
    },
    // 是否可搜索
    filterable: {
      type: Boolean,
      default: true
    },
    // 是否显示清除按钮
    clearable: {
      type: Boolean,
      default: true
    },
    // 是否显示加载中状态
    loading: {
      type: Boolean,
      default: false
    },
    // 是否显示全选按钮
    showSelectAll: {
      type: Boolean,
    },
    // 显示的文本字段名
    labelKey: {
      type: String,
      default: 'ORMTB410CA00'
    },
    // 值字段名
    valueKey: {
      type: String,
      default: 'ORMTB410ID'
    },
    // 拼音字段名（用于搜索）
    pinyinKey: {
      type: String,
      default: 'ORMTB410CA13'
    },
    // 是否开启筛选
    filterable: {
      type: Boolean,
      default: true
    },
    // 占位文本
    placeholder: {
      type: String,
      default: '请选择'
    },
    // 自定义类名
    className: {
      type: String,
      default: 'public_optionSty dropDown_Define'
    },
    // 是否启用拼音搜索
    enablePinyinSearch: {
      type: Boolean,
      default: true
    },
    // 是否启用首字母搜索
    enableFirstLetterSearch: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      displayOptions: [],  // 过滤后显示的数据
    }
  },
  
  computed: {
    selectedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    }
  },
  
  watch: {
    options: {
      handler(newVal) {
        this.displayOptions = [...newVal];
      },
      immediate: true,
      deep: true
    }
  },
  
  mounted() {
    this.initDisplayOptions();
  },
  
  methods: {
    /**
     * 初始化显示选项
     */
    initDisplayOptions() {
      this.displayOptions = [...this.options];
    },
    
    /**
     * 拼音首字母匹配
     * @param {string} pinyin - 完整拼音字符串
     * @param {string} query - 搜索关键词
     * @returns {boolean}
     */
    matchFirstLetter(pinyin, query) {
      if (!pinyin || !query) return false;
      
      // 提取拼音首字母（例如："zhong guo" -> "zg"）
      const firstLetters = pinyin
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toLowerCase();
      
      return firstLetters.includes(query);
    },
    
    /**
     * 检查是否匹配搜索条件
     * @param {object} item - 数据项
     * @param {string} query - 搜索关键词
     * @returns {boolean}
     */
    isMatch(item, query) {
      // 获取标签值（显示文本）
      const labelValue = item[this.labelKey];
      // 获取拼音值
      const pinyinValue = item[this.pinyinKey];
      
      // 匹配标签（中文名称）
      if (labelValue && labelValue.toLowerCase().indexOf(query) !== -1) {
        return true;
      }
      
      // 匹配拼音
      if (this.enablePinyinSearch && pinyinValue) {
        if (pinyinValue.toLowerCase().indexOf(query) !== -1) {
          return true;
        }
      }
      
      // 匹配拼音首字母
      if (this.enableFirstLetterSearch && pinyinValue) {
        if (this.matchFirstLetter(pinyinValue, query)) {
          return true;
        }
      }
      
      return false;
    },
    
    /**
     * 自定义过滤方法
     * @param {string} query - 用户输入的搜索关键词
     */
    customFilterMethod(query) {
      // 如果没有输入搜索词或未开启筛选，显示全部数据
      if (!this.filterable || !query || query.trim() === '') {
        this.displayOptions = [...this.options];
        return;
      }
      
      const lowerQuery = query.toLowerCase().trim();
      const result = [];
      
      for (let i = 0; i < this.options.length; i++) {
        const item = this.options[i];
        if (this.isMatch(item, lowerQuery)) {
          result.push(item);
        }
      }
      
      this.displayOptions = result;
    },
    
    /**
     * 下拉框显示/隐藏时的回调
     * @param {boolean} visible - true: 显示, false: 隐藏
     */
    handleVisibleChange(visible) {
      // 当下拉框关闭且没有选中值时，重置选项列表（可选）
      if (!visible) {
        this.displayOptions = [...this.options];
      }
      this.$emit('visible-change', visible);
    },
    
    /**
     * 选择变化时的处理（新增）
     * @param {string|number} value - 选中的值
     */
    handleChange(value) {
      // 获取选中的完整对象
      const selectedItem = this.getSelectedItem(value);
      
      // 触发 change 事件，传递值和完整对象给父组件
      this.$emit('change', value, selectedItem);
      
      // 同时触发自定义的 on-change 事件，方便父组件使用
      this.$emit('on-change', value, selectedItem);
    },
    
    /**
     * 获取当前选中的项（完整对象）
     * @param {string|number} value - 可选，指定要获取的值，默认使用当前选中值
     * @returns {object|null}
     */
    getSelectedItem(value) {
      const targetValue = value !== undefined ? value : this.selectedValue;
      const selected = this.options.find(
        item => item[this.valueKey] === targetValue
      );
      return selected || null;
    },
    
    /**
     * 重置筛选
     */
    resetFilter() {
      this.displayOptions = [...this.options];
    },
    
    /**
     * 手动设置选项列表（用于异步加载后刷新）
     */
    setOptions(newOptions) {
      this.$emit('update:options', newOptions);
    }
  }
}
</script>